import { DocumentTemplateType, FDItemType } from "@prisma/client-generated";

/**
 * The set of field names that constitute joint-ownership data.
 * Used for allowlist checks (e.g. partner-only patches).
 */
export const JOINT_OWNERSHIP_FIELDS: readonly string[] = [
  "isJoint",
  "userAPercentage",
  "userBPercentage",
];

/**
 * For each `FDItemType`, the value is the allowlist of document template
 * types on which joint ownership (the `isJoint` / percentage split) is
 * enabled.
 *
 * Declared with `as const satisfies` so each value's tuple length is
 * preserved at the type level: downstream code (e.g. the joint-percentage
 * swap registry in `doc-swap.ts`) uses `extends readonly []` to derive
 * which FDItemTypes are joint-capable, which only works when the array
 * literal types aren't widened to `readonly DocumentTemplateType[]`.
 */
export const JOINT_OWNERSHIP_DOC_TYPES_BY_FD_ITEM_TYPE = {
  [FDItemType.ACCOUNTS]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.ASSETS]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.VEHICLES]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.REAL_ESTATE]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.BUSINESS]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.DEBTS]: [
    DocumentTemplateType.PRENUP,
    DocumentTemplateType.POSTNUP,
  ],
  [FDItemType.INHERITANCE]: [DocumentTemplateType.POSTNUP],
  [FDItemType.TRUSTS]: [DocumentTemplateType.POSTNUP],
  [FDItemType.INCOME]: [],
  [FDItemType.CHILD_ACCOUNTS]: [],
  [FDItemType.LEGACY_JOINT]: [],
} as const satisfies Record<FDItemType, readonly DocumentTemplateType[]>;

/** True when joint ownership is enabled for this FD item type on the given
 *  document template type. */
export function isJointOwnershipAllowedForDocType(
  fdItemType: FDItemType,
  docType: DocumentTemplateType,
): boolean {
  // Widened to a plain readonly array so `.includes` accepts a generic
  // `DocumentTemplateType`; the `as const` tuple types preserved by the
  // declaration above would otherwise narrow `.includes`'s parameter to
  // the specific literal union of each tuple's members.
  const allowed: readonly DocumentTemplateType[] =
    JOINT_OWNERSHIP_DOC_TYPES_BY_FD_ITEM_TYPE[fdItemType];
  return allowed.includes(docType);
}

interface JointOwnershipData {
  isJoint?: boolean | undefined;
  userAPercentage?: number | null | undefined;
  userBPercentage?: number | null | undefined;
}

export function normalizeJointOwnership<T extends JointOwnershipData>(
  data: T,
  options: { defaultIsJointToFalse: boolean },
): T {
  if (data.isJoint === true) {
    return data;
  }

  const shouldClearPercentages =
    data.isJoint === false || options.defaultIsJointToFalse;

  if (!shouldClearPercentages) {
    return data;
  }

  return {
    ...data,
    ...(options.defaultIsJointToFalse ? { isJoint: false } : {}),
    userAPercentage: null,
    userBPercentage: null,
  };
}

export function hasJointOwnershipFields(data: JointOwnershipData): boolean {
  return (
    data.isJoint !== undefined ||
    data.userAPercentage !== undefined ||
    data.userBPercentage !== undefined
  );
}

export function isJointOwnershipOnlyPatch(
  data: Record<string, unknown>,
): boolean {
  return Object.keys(data).every((key) => JOINT_OWNERSHIP_FIELDS.includes(key));
}

export interface JointOwnershipFields {
  isJoint?: boolean | undefined;
  userAPercentage?: number | null | undefined;
  userBPercentage?: number | null | undefined;
}

function isValidJointField(
  v: unknown,
): v is boolean | number | null | undefined {
  return (
    v === undefined ||
    v === null ||
    typeof v === "boolean" ||
    typeof v === "number"
  );
}

export function hasJointShape(data: unknown): data is JointOwnershipFields {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    isValidJointField(d["isJoint"]) &&
    isValidJointField(d["userAPercentage"]) &&
    isValidJointField(d["userBPercentage"])
  );
}

export function mergeJointFields(
  updated: JointOwnershipFields,
  existing: JointOwnershipFields,
): JointOwnershipFields {
  return {
    isJoint: updated.isJoint ?? existing.isJoint ?? undefined,
    userAPercentage:
      updated.userAPercentage ?? existing.userAPercentage ?? undefined,
    userBPercentage:
      updated.userBPercentage ?? existing.userBPercentage ?? undefined,
  };
}

export function getJointOwnershipValidationError(
  data: JointOwnershipData,
  options?: { requirePercentages?: boolean },
): string | null {
  if (data.isJoint !== true) {
    return null;
  }

  // Postnup allows isJoint=true with null percentages; prenup requires them.
  const requirePercentages = options?.requirePercentages ?? true;

  const userA = data.userAPercentage;
  const userB = data.userBPercentage;
  const userAProvided = typeof userA === "number";
  const userBProvided = typeof userB === "number";

  if (!userAProvided && !userBProvided) {
    return requirePercentages ? "User percentages must add up to 100%" : null;
  }

  // A partially-filled split is always invalid: either both percentages
  // must be absent (postnup only) or both must be present and sum to 100.
  if (!userAProvided || !userBProvided) {
    return "User percentages must add up to 100%";
  }

  if (userA + userB !== 100) {
    return "User percentages must add up to 100%";
  }

  return null;
}
